import { useState, useEffect, useCallback } from 'react';
import { Settings, TrendingUp, Info, Plus, Trash2, GripVertical, AlertCircle, CheckCircle2, BarChart3, Users, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { UserCombobox } from '@/components/ui/user-combobox';
import { TemplateCombobox } from '@/components/ui/template-combobox';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useUsersList } from '@/hooks/useUsersData';
import { useTemplatesList } from '@/hooks/useTemplatesData';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface AgeingBand {
  id: string;
  name: string;
  startDay: number;
  endDay: number;
  color: string;
  alertEnabled: boolean;
  assignedCreditController: boolean;
  alertUserId?: string;
}

const DEFAULT_BANDS: AgeingBand[] = [
  {
    id: '1',
    name: 'Current',
    startDay: 0,
    endDay: 30,
    color: '#5eead4', // teal-300
    alertEnabled: false,
    assignedCreditController: false,
  },
  {
    id: '2',
    name: '31-60 Days',
    startDay: 31,
    endDay: 60,
    color: '#2dd4bf', // teal-400
    alertEnabled: false,
    assignedCreditController: false,
  },
  {
    id: '3',
    name: '61-90 Days',
    startDay: 61,
    endDay: 90,
    color: '#14b8a6', // teal-500
    alertEnabled: false,
    assignedCreditController: false,
  },
  {
    id: '4',
    name: '91-120 Days',
    startDay: 91,
    endDay: 120,
    color: '#0d9488', // teal-600
    alertEnabled: false,
    assignedCreditController: false,
  },
  {
    id: '5',
    name: 'Over 120 Days',
    startDay: 121,
    endDay: 999,
    color: '#0f766e', // teal-700
    alertEnabled: false,
    assignedCreditController: false,
  },
];

export function GeneralSettingsPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [bands, setBands] = useState<AgeingBand[]>(DEFAULT_BANDS);
  const [dsoTarget, setDsoTarget] = useState<number>(40);
  const [hasChanges, setHasChanges] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  // Category Keys state
  const [categoryKeys, setCategoryKeys] = useState({
    userSortKey1Label: 'User Sort Key 1',
    userSortKey2Label: 'User Sort Key 2',
    userSortKey3Label: 'User Sort Key 3',
    userSortKey4Label: 'User Sort Key 4',
  });

  // On Stop Notes state
  const [onStopNotesEnabled, setOnStopNotesEnabled] = useState(false);

  // Copy Invoice Email Template state
  const [copyInvoiceTemplateId, setCopyInvoiceTemplateId] = useState<string>('');

  // Risk & Credit - Credit Limit Warnings state
  const [creditLimitExceededEnabled, setCreditLimitExceededEnabled] = useState(false);
  const [creditLimitRecipientType, setCreditLimitRecipientType] = useState<'assigned' | 'specific'>('assigned');
  const [creditLimitRecipientUserId, setCreditLimitRecipientUserId] = useState<string>('');
  const [creditWarningThreshold, setCreditWarningThreshold] = useState<number>(80);

  // Fetch users for alert assignment
  const { data: users = [] } = useUsersList();

  // Fetch templates for copy invoice setting
  const { data: templates = [] } = useTemplatesList();

  const validateBands = useCallback((): string[] => {
    const errors: string[] = [];

    if (bands.length < 1 || bands.length > 10) {
      errors.push('You must have between 1 and 10 ageing bands');
    }

    bands.forEach((band, index) => {
      if (!band.name.trim()) {
        errors.push(`Band ${index + 1}: Name is required`);
      }

      if (band.startDay < 0) {
        errors.push(`Band ${index + 1}: Start day must be 0 or greater`);
      }

      if (band.endDay <= band.startDay) {
        errors.push(`Band ${index + 1}: End day must be greater than start day`);
      }

      if (band.alertEnabled && !band.assignedCreditController && !band.alertUserId) {
        errors.push(`Band ${index + 1}: Alert user must be selected when alerts are enabled`);
      }

      // Check for gaps between bands
      if (index > 0) {
        const prevBand = bands[index - 1];
        if (band.startDay !== prevBand.endDay + 1) {
          errors.push(
            `Gap detected between "${prevBand.name}" and "${band.name}". End day of previous band (${prevBand.endDay}) must connect to start day of next band (${band.startDay})`
          );
        }
      }
    });

    return errors;
  }, [bands]);

  // Real-time validation
  useEffect(() => {
    const errors = validateBands();
    setValidationErrors(errors);
  }, [bands, dsoTarget, validateBands]);

  const handleAddBand= () => {
    if (bands.length >= 10) {
      toast.error('Maximum of 10 bands allowed');
      return;
    }

    const lastBand = bands[bands.length - 1];
    const newBand: AgeingBand = {
      id: Date.now().toString(),
      name: `Band ${bands.length + 1}`,
      startDay: lastBand ? lastBand.endDay + 1 : 0,
      endDay: lastBand ? lastBand.endDay + 30 : 30,
      color: '#6b7280', // gray
      alertEnabled: false,
      assignedCreditController: false,
    };

    setBands([...bands, newBand]);
    setHasChanges(true);
  };

  const handleRemoveBand = (id: string) => {
    if (bands.length <= 1) {
      toast.error('At least one band is required');
      return;
    }

    setBands(bands.filter((band) => band.id !== id));
    setHasChanges(true);
  };

  const handleBandChange = (id: string, field: keyof AgeingBand, value: string | number | boolean | undefined) => {
    setBands(
      bands.map((band) =>
        band.id === id
          ? {
              ...band,
              [field]: value,
            }
          : band
      )
    );
    setHasChanges(true);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newBands = [...bands];
    const draggedBand = newBands[draggedIndex];
    newBands.splice(draggedIndex, 1);
    newBands.splice(index, 0, draggedBand);

    setBands(newBands);
    setDraggedIndex(index);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleKeyboardReorder = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= bands.length) return;
    const newBands = [...bands];
    const movedBand = newBands[index];
    newBands.splice(index, 1);
    newBands.splice(targetIndex, 0, movedBand);
    setBands(newBands);
    setHasChanges(true);
  };

  const handleSave = async () => {
    const errors = validateBands();

    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
      return;
    }

    if (dsoTarget <= 0) {
      toast.error('DSO target must be a positive number');
      return;
    }

    try {
      // Placeholder: API integration pending
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate API call
      console.log('Saving settings:', { 
        dashboard: { bands, dsoTarget },
        customers: { categoryKeys, onStopNotesEnabled, copyInvoiceTemplateId },
        riskCredit: { 
          creditLimitExceededEnabled, 
          creditLimitRecipientType, 
          creditLimitRecipientUserId, 
          creditWarningThreshold 
        }
      });

      let successMessage = 'Risk & Credit settings saved successfully';
      if (activeTab === 'dashboard') {
        successMessage = 'Dashboard settings saved successfully';
      } else if (activeTab === 'customers') {
        successMessage = 'Customer settings saved successfully';
      }
      let description = 'Credit limit warning settings have been updated';
      if (activeTab === 'dashboard') {
        description = 'Changes will apply to all users\' dashboards';
      } else if (activeTab === 'customers') {
        description = 'Category key labels have been updated';
      }

      toast.success(successMessage, {
        description,
        icon: <CheckCircle2 className="h-5 w-5" />,
      });

      setHasChanges(false);
    } catch {
      toast.error('Failed to save settings', {
        description: 'Please try again or contact support',
      });
    }
  };

  const handleCancel = () => {
    setBands(DEFAULT_BANDS);
    setDsoTarget(40);
    setCategoryKeys({
      userSortKey1Label: 'User Sort Key 1',
      userSortKey2Label: 'User Sort Key 2',
      userSortKey3Label: 'User Sort Key 3',
      userSortKey4Label: 'User Sort Key 4',
    });
    setOnStopNotesEnabled(false);
    setCopyInvoiceTemplateId('');
    setCreditLimitExceededEnabled(false);
    setCreditLimitRecipientType('assigned');
    setCreditLimitRecipientUserId('');
    setCreditWarningThreshold(80);
    setHasChanges(false);
    toast.info('Changes discarded');
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="container max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Settings className="h-6 w-6 text-primary" />
              <h1 className="text-3xl font-bold tracking-tight">General Settings</h1>
            </div>
            <p className="text-muted-foreground">
              Configure system-wide settings and preferences
            </p>
          </div>
        </div>

        {/* Settings Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="dashboard" className="gap-2">
              <TrendingUp className="h-4 w-4" />
              Metrics
            </TabsTrigger>
            <TabsTrigger value="customers" className="gap-2">
              <Users className="h-4 w-4" />
              Customers
            </TabsTrigger>
            <TabsTrigger value="riskCredit" className="gap-2">
              <ShieldAlert className="h-4 w-4" />
              Risk & Credit
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            {/* Info Alert */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                These settings apply globally to all users' dashboards. Changes will take effect immediately after saving.
              </AlertDescription>
            </Alert>

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-1">
                    <p className="font-semibold">Please fix the following issues:</p>
                    <ul className="list-disc list-inside space-y-0.5 text-sm">
                      {validationErrors.map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-muted-foreground">Configured Bands</p>
                      <p className="text-2xl font-bold">{bands.length}</p>
                    </div>
                    <div className="rounded-full bg-primary/10 p-3">
                      <BarChart3 className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-muted-foreground">Active Alerts</p>
                      <p className="text-2xl font-bold">{bands.filter((b) => b.alertEnabled).length}</p>
                    </div>
                    <div className="rounded-full bg-amber-500/10 p-3">
                      <AlertCircle className="h-6 w-6 text-amber-600 dark:text-amber-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-muted-foreground">Target DSO</p>
                      <p className="text-2xl font-bold">{dsoTarget} days</p>
                    </div>
                    <div className="rounded-full bg-teal-500/10 p-3">
                      <TrendingUp className="h-6 w-6 text-teal-600 dark:text-teal-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* DSO Target Configuration */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start gap-6">
                  <div className="flex-1 space-y-4">
                    <div>
                      <CardTitle>DSO Target Configuration</CardTitle>
                      <CardDescription>
                        Set the global Days Sales Outstanding (DSO) target displayed in the Performance Snapshot section
                      </CardDescription>
                    </div>
                    
                    <div className="max-w-xs space-y-2">
                      <Label htmlFor="dso-target">Target DSO (Days)</Label>
                      <Input
                        id="dso-target"
                        type="number"
                        min="1"
                        value={dsoTarget}
                        onChange={(e) => {
                          setDsoTarget(parseInt(e.target.value) || 0);
                          setHasChanges(true);
                        }}
                        placeholder="Enter target DSO in days"
                      />
                      <p className="text-sm text-muted-foreground">
                        Default: 40 days. Must be a positive integer.
                      </p>
                    </div>
                  </div>
                  
                  {/* DSO Calculation Information Panel */}
                  <Alert variant="info" className="max-w-xl flex-shrink-0">
                    <AlertTitle>How DSO is Calculated</AlertTitle>
                    <AlertDescription>
                      <p className="mb-2">
                        <span className="font-semibold text-base">DSO = (Outstanding Debt ÷ Sales) × Days in Period</span>
                      </p>
                      <p>
                        Shows average days to collect payment. Calculated automatically from Access Financials data.
                      </p>
                    </AlertDescription>
                  </Alert>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
              </CardContent>
            </Card>

            {/* Ageing Analysis Bands Configuration */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle>Ageing Analysis Bands Configuration</CardTitle>
                    <CardDescription>
                      Define custom ageing analysis bands for dashboard visualizations (1-10 bands)
                    </CardDescription>
                  </div>
                  <Button onClick={handleAddBand} size="sm" disabled={bands.length >= 10}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Band
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Visual Preview */}
                <div className="space-y-3">
                  <Label className="text-base">Visual Preview</Label>
                  <div className="flex gap-2 h-12 rounded-lg overflow-hidden border">
                    {bands.map((band) => (
                      <div
                        key={band.id}
                        className="flex-1 flex items-center justify-center text-xs font-medium text-white relative group transition-all hover:scale-105"
                        style={{ backgroundColor: band.color }}
                        title={`${band.name}: ${band.startDay}-${band.endDay} days`}
                      >
                        <span className="truncate px-2">{band.name}</span>
                        {band.alertEnabled && (
                          <AlertCircle className="h-3 w-3 absolute top-1 right-1" />
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    This is how your ageing bands will appear in charts and visualizations
                  </p>
                </div>
                {/* Bands List */}
                <ul className="space-y-3 list-none p-0 m-0">
                  {bands.map((band, index) => (
                    <li
                      key={band.id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={cn(
                        'border rounded-lg p-4 space-y-4 bg-card transition-all',
                        draggedIndex === index && 'opacity-50',
                        'hover:border-primary/50 cursor-move hover:shadow-md'
                      )}
                    >
                      {/* Band Header with Number */}
                      <div className="flex items-center gap-2 pb-2 border-b">
                        <Badge variant="outline" className="font-mono">
                          Band {index + 1}
                        </Badge>
                        {band.alertEnabled && (
                          <Badge variant="secondary" className="gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Alert Enabled
                          </Badge>
                        )}
                        <div className="ml-auto flex items-center gap-2">
                          <div
                            className="w-6 h-6 rounded border-2 border-white shadow-sm"
                            style={{ backgroundColor: band.color }}
                            title={band.color}
                          />
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        {/* Drag Handle */}
                        <div className="flex items-center pt-8">
                          <button
                            type="button"
                            aria-label={`Reorder band ${index + 1}`}
                            onKeyDown={(e) => {
                              if (e.key === 'ArrowUp' && index > 0) {
                                e.preventDefault();
                                handleKeyboardReorder(index, 'up');
                              } else if (e.key === 'ArrowDown' && index < bands.length - 1) {
                                e.preventDefault();
                                handleKeyboardReorder(index, 'down');
                              }
                            }}
                            className="cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-primary rounded"
                          >
                            <GripVertical className="h-5 w-5 text-muted-foreground hover:text-primary" />
                          </button>
                        </div>

                        {/* Band Configuration */}
                        <div className="flex-1 space-y-4">
                          {/* Row 1: Band Name, Start Day, End Day, Color */}
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor={`band-name-${band.id}`}>
                                Band Name <span className="text-destructive">*</span>
                              </Label>
                              <Input
                                id={`band-name-${band.id}`}
                                value={band.name}
                                onChange={(e) => handleBandChange(band.id, 'name', e.target.value)}
                                placeholder="e.g., Current"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor={`band-start-${band.id}`}>
                                Start Day <span className="text-destructive">*</span>
                              </Label>
                              <Input
                                id={`band-start-${band.id}`}
                                type="number"
                                min="0"
                                value={band.startDay}
                                onChange={(e) =>
                                  handleBandChange(band.id, 'startDay', parseInt(e.target.value) || 0)
                                }
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor={`band-end-${band.id}`}>
                                End Day <span className="text-destructive">*</span>
                              </Label>
                              <Input
                                id={`band-end-${band.id}`}
                                type="number"
                                min="0"
                                value={band.endDay}
                                onChange={(e) =>
                                  handleBandChange(band.id, 'endDay', parseInt(e.target.value) || 0)
                                }
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor={`band-color-${band.id}`}>
                                Color <span className="text-destructive">*</span>
                              </Label>
                              <div className="flex gap-2">
                                <Input
                                  id={`band-color-${band.id}`}
                                  type="color"
                                  value={band.color}
                                  onChange={(e) => handleBandChange(band.id, 'color', e.target.value)}
                                  className="w-20 cursor-pointer"
                                />
                                <div
                                  className="flex-1 rounded-md border"
                                  style={{ backgroundColor: band.color }}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Row 2: Alert Configuration */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor={`band-alert-${band.id}`}>Enable Feed Notification Alert</Label>
                              <div className="flex items-center space-x-2">
                                <Switch
                                  id={`band-alert-${band.id}`}
                                  checked={band.alertEnabled}
                                  onCheckedChange={(checked) => handleBandChange(band.id, 'alertEnabled', checked)}
                                />
                                <span className="text-sm text-muted-foreground">
                                  {band.alertEnabled ? 'Alert enabled' : 'Alert disabled'}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Send notifications when invoices fall into this band
                              </p>
                            </div>

                            {band.alertEnabled && (
                              <>
                                {/* Assigned Credit Controller Checkbox */}
                                <div className="space-y-2">
                                  <Label htmlFor={`band-assigned-cc-${band.id}`}>Assigned Credit Controller</Label>
                                  <div className="flex items-center space-x-2 h-10">
                                    <Checkbox
                                      id={`band-assigned-cc-${band.id}`}
                                      checked={band.assignedCreditController}
                                      onCheckedChange={(checked) => {
                                        const isChecked = checked === true;
                                        // Update both fields in a single state update
                                        setBands(
                                          bands.map((b) =>
                                            b.id === band.id
                                              ? {
                                                  ...b,
                                                  assignedCreditController: isChecked,
                                                  alertUserId: isChecked ? undefined : b.alertUserId,
                                                }
                                              : b
                                          )
                                        );
                                        setHasChanges(true);
                                      }}
                                    />
                                    <Label
                                      htmlFor={`band-assigned-cc-${band.id}`}
                                      className="text-sm font-normal cursor-pointer"
                                    >
                                      Use assigned controller
                                    </Label>
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    Alert the customer's assigned credit controller
                                  </p>
                                </div>

                                {/* Alert Recipient Field */}
                                <div className="space-y-2">
                                  <Label htmlFor={`band-alert-user-${band.id}`}>
                                    Alert Recipient {!band.assignedCreditController && <span className="text-destructive">*</span>}
                                  </Label>
                                  <UserCombobox
                                    users={users}
                                    value={band.alertUserId}
                                    onValueChange={(userId) => handleBandChange(band.id, 'alertUserId', userId)}
                                    placeholder="Select user to receive alerts..."
                                    emptyText="No users found"
                                    disabled={band.assignedCreditController}
                                  />
                                  {!band.assignedCreditController && !band.alertUserId && (
                                    <div className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-500">
                                      <AlertCircle className="h-3 w-3" />
                                      <span>Alert recipient is required when alerts are enabled</span>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Remove Button */}
                        <div className="flex items-center pt-8">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveBand(band.id)}
                            disabled={bands.length <= 1}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Band Count Info */}
                <div className="flex items-center justify-between text-sm text-muted-foreground pt-2">
                  <span>
                    {bands.length} of 10 bands configured
                  </span>
                  <span>Drag bands to reorder</span>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={handleCancel} disabled={!hasChanges}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!hasChanges}>
                Save Changes
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="customers" className="space-y-6">
            {/* Info Alert */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                These settings apply to all customer interactions. Changes will take effect immediately after saving.
              </AlertDescription>
            </Alert>

            {/* On Stop Notes Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>On Stop Notes</CardTitle>
                <CardDescription>
                  Configure whether users are prompted to add a note when placing a customer on stop
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="onStopNotes" className="text-base">
                      Require Note When Placing Customer On Stop
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      When enabled, users will be prompted to add a note explaining why a customer is being placed on stop. The note will be automatically saved to the customer's Notes tab.
                    </p>
                  </div>
                  <Switch
                    id="onStopNotes"
                    checked={onStopNotesEnabled}
                    onCheckedChange={(checked) => {
                      setOnStopNotesEnabled(checked);
                      setHasChanges(true);
                    }}
                  />
                </div>
              </CardContent>
            </Card>


            {/* Category Keys Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>Category Keys</CardTitle>
                <CardDescription>
                  Customize the labels for User Sort Key fields displayed in Customer Details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="userSortKey1Label">User Sort Key 1 Label</Label>
                    <Input
                      id="userSortKey1Label"
                      value={categoryKeys.userSortKey1Label}
                      onChange={(e) => {
                        setCategoryKeys({ ...categoryKeys, userSortKey1Label: e.target.value });
                        setHasChanges(true);
                      }}
                      placeholder="Enter custom label"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="userSortKey2Label">User Sort Key 2 Label</Label>
                    <Input
                      id="userSortKey2Label"
                      value={categoryKeys.userSortKey2Label}
                      onChange={(e) => {
                        setCategoryKeys({ ...categoryKeys, userSortKey2Label: e.target.value });
                        setHasChanges(true);
                      }}
                      placeholder="Enter custom label"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="userSortKey3Label">User Sort Key 3 Label</Label>
                    <Input
                      id="userSortKey3Label"
                      value={categoryKeys.userSortKey3Label}
                      onChange={(e) => {
                        setCategoryKeys({ ...categoryKeys, userSortKey3Label: e.target.value });
                        setHasChanges(true);
                      }}
                      placeholder="Enter custom label"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="userSortKey4Label">User Sort Key 4 Label</Label>
                    <Input
                      id="userSortKey4Label"
                      value={categoryKeys.userSortKey4Label}
                      onChange={(e) => {
                        setCategoryKeys({ ...categoryKeys, userSortKey4Label: e.target.value });
                        setHasChanges(true);
                      }}
                      placeholder="Enter custom label"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={handleCancel} disabled={!hasChanges}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!hasChanges}>
                Save Changes
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="riskCredit" className="space-y-6">
            {/* Info Alert */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Configure credit limit monitoring and notification settings. These settings help you proactively manage customer credit risk.
              </AlertDescription>
            </Alert>

            {/* Credit Limit Warnings Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>Credit Limit Warnings</CardTitle>
                <CardDescription>
                  Configure notifications for when customers exceed or approach their credit limits
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Credit Limit Exceeded Notification */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-4 border-b">
                    <div className="space-y-0.5">
                      <Label htmlFor="creditLimitExceeded" className="text-base">
                        Credit Limit Exceeded Notification
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Send feed notifications when a customer exceeds their credit limit
                      </p>
                    </div>
                    <Switch
                      id="creditLimitExceeded"
                      checked={creditLimitExceededEnabled}
                      onCheckedChange={(checked) => {
                        setCreditLimitExceededEnabled(checked);
                        setHasChanges(true);
                      }}
                    />
                  </div>

                  {/* Recipient Configuration - Only shown when toggle is ON */}
                  {creditLimitExceededEnabled && (
                    <div className="space-y-4 pl-4 border-l-2 border-primary/20">
                      <div className="space-y-3">
                        <Label className="text-base">Notification Recipient</Label>
                        <p className="text-sm text-muted-foreground">
                          Choose who should receive credit limit exceeded notifications
                        </p>
                        
                        <RadioGroup
                          value={creditLimitRecipientType}
                          onValueChange={(value) => {
                            setCreditLimitRecipientType(value as 'assigned' | 'specific');
                            if (value === 'assigned') {
                              setCreditLimitRecipientUserId('');
                            }
                            setHasChanges(true);
                          }}
                          className="space-y-3"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="assigned" id="recipient-assigned" />
                            <Label htmlFor="recipient-assigned" className="font-normal cursor-pointer">
                              Assigned Credit Controller for that customer
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="specific" id="recipient-specific" />
                            <Label htmlFor="recipient-specific" className="font-normal cursor-pointer">
                              Specific Recipient
                            </Label>
                          </div>
                        </RadioGroup>

                        {/* User Dropdown - Only shown when "Specific Recipient" is selected */}
                        {creditLimitRecipientType === 'specific' && (
                          <div className="space-y-2 pl-6">
                            <Label htmlFor="creditLimitRecipient">
                              Select User <span className="text-destructive">*</span>
                            </Label>
                            <UserCombobox
                              users={users}
                              value={creditLimitRecipientUserId}
                              onValueChange={(userId) => {
                                setCreditLimitRecipientUserId(userId);
                                setHasChanges(true);
                              }}
                              placeholder="Select user to receive notifications..."
                              emptyText="No users found"
                            />
                            <p className="text-xs text-muted-foreground">
                              This user will receive all credit limit exceeded notifications
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Notification Message Preview */}
                      <Alert className="bg-muted/50">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Notification Message Preview</AlertTitle>
                        <AlertDescription>
                          <p className="text-sm mt-2">
                            <span className="font-semibold">[Customer Name]</span> has exceeded their credit limit.
                          </p>
                          <p className="text-sm mt-1">
                            Credit Limit: <span className="font-semibold">£10,000.00</span> | 
                            Current Outstanding: <span className="font-semibold text-destructive">£12,500.00</span>
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            Clicking the notification will navigate directly to the customer's profile
                          </p>
                        </AlertDescription>
                      </Alert>
                    </div>
                  )}
                </div>

                {/* Credit Warning Threshold */}
                <div className="space-y-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label 
                      htmlFor="creditWarningThreshold" 
                      className={cn(
                        "text-base",
                        !creditLimitExceededEnabled && "text-muted-foreground"
                      )}
                    >
                      Credit Warning Threshold
                    </Label>
                    <p className={cn(
                      "text-sm text-muted-foreground",
                      !creditLimitExceededEnabled && "text-muted-foreground/60"
                    )}>
                      Set the percentage of credit limit utilization that triggers an early warning notification
                    </p>
                  </div>

                  <div className="max-w-xs space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        id="creditWarningThreshold"
                        type="number"
                        min="1"
                        max="100"
                        value={creditWarningThreshold}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0;
                          setCreditWarningThreshold(Math.min(100, Math.max(1, value)));
                          setHasChanges(true);
                        }}
                        disabled={!creditLimitExceededEnabled}
                        className="w-24"
                      />
                      <span className={cn(
                        "text-sm font-medium",
                        !creditLimitExceededEnabled && "text-muted-foreground"
                      )}>
                        %
                      </span>
                    </div>
                    <p className={cn(
                      "text-xs text-muted-foreground",
                      !creditLimitExceededEnabled && "text-muted-foreground/60"
                    )}>
                      Default: 80%. Notifications will be sent to the same recipient configured above.
                    </p>
                  </div>

                  {creditLimitExceededEnabled && (
                    <Alert variant="info" className="max-w-2xl">
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        <p className="text-sm">
                          <span className="font-semibold">Example:</span> With a threshold of {creditWarningThreshold}%, 
                          a customer with a £10,000 credit limit will trigger a warning notification when their 
                          outstanding balance reaches £{((creditWarningThreshold / 100) * 10000).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.
                        </p>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={handleCancel} disabled={!hasChanges}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!hasChanges}>
                Save Changes
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
